/**
 * Types para componentes de mídia do chat.
 * Centraliza tipos e utilitários compartilhados.
 *
 * URL efêmera vs exibível: use isDisplayableMediaUrl / isTemporaryRemoteMediaUrl
 * em utils/failedImageCache (fonte única).
 */

export interface MediaInfo {
    url?: string;
    mimetype?: string;
    duration?: number;
    /** Transcrição de áudio via speechToText */
    transcription?: string | null;
}

export interface MessageMediaProps {
    type: string;
    media?: MediaInfo;
    content?: string;
    isMe?: boolean;
    senderName?: string;
    senderAvatar?: string;
    timestamp?: string | Date;
    status?: string;
    chatPlatform?: 'whatsapp' | 'instagram';
}

/** MIME para <source type> — Safari trata .mpeg sem type como vídeo. */
export function audioSourceType(url?: string, mimetype?: string): string | undefined {
    const mime = (mimetype || '').toLowerCase().split(';')[0].trim();
    if (mime.startsWith('audio/')) {
        if (mime === 'audio/mp3') return 'audio/mpeg';
        return mime;
    }
    const u = (url || '').toLowerCase();
    if (/\.(mp3|mpeg)(\?|$)/.test(u)) return 'audio/mpeg';
    if (/\.(m4a|aac)(\?|$)/.test(u)) return 'audio/mp4';
    if (/\.(ogg|oga|opus)(\?|$)/.test(u)) return 'audio/ogg';
    if (/\.webm(\?|$)/.test(u)) return 'audio/webm';
    return undefined;
}

/**
 * Utils para parse seguro de JSON
 */
export const safeParse = (jsonString?: string): any => {
    if (!jsonString) return null;
    try {
        return JSON.parse(jsonString);
    } catch {
        return null;
    }
};

/**
 * Utils para extrair telefone do VCard
 */
export const extractPhoneFromVCard = (vcard: string): string | null => {
    if (!vcard) return null;

    // 1. Tenta pegar o ID do WhatsApp (waid) que é o mais seguro
    const waidMatch = vcard.match(/waid=(\d+)/i);
    if (waidMatch) return waidMatch[1];

    // 2. Tenta pegar o campo TEL genérico
    const telMatch = vcard.match(/(?:TEL|tel)[^:]*:(.*)/i);
    if (telMatch) {
        // Limpa caracteres não numéricos se tiver muitos (mantém +)
        return telMatch[1]?.replace(/[^0-9+]/g, '') || null;
    }

    return null;
};
